import { configureApiFetch } from '@api';
import { createContext, ReactNode, useContext, useLayoutEffect } from 'react';

/**
 * Host-injected API configuration for design-system **data providers**
 * (`AssetHeaderDataProvider`, etc.). Not part of the generative-UI JSON
 * contract — the LLM must never emit `basePath` or `token`.
 *
 * **Who wires it:** terminal wraps the generative-UI
 * render tree, e.g. around Alfred `Messages` / `Research`, with real values
 * from `config.api.root` and the auth store.
 *
 * **Who reads it:** mounting this provider calls `configureApiFetch` so
 * `@api` helpers (`postEntity`, etc.) pick up `basePath` and `token` without
 * per-call threading. `useApiConfig()` remains available when a component
 * needs the values directly.
 */
export interface ApiConfig {
  basePath: string;
  token?: string;
}

const ApiConfigContext = createContext<ApiConfig>({ basePath: '/' });

export interface ApiConfigProviderProps extends Partial<ApiConfig> {
  children: ReactNode;
}

export const ApiConfigProvider = ({
  basePath = '/',
  token,
  children,
}: ApiConfigProviderProps) => {
  useLayoutEffect(() => {
    configureApiFetch({ basePath, token });
  }, [basePath, token]);

  return (
    <ApiConfigContext.Provider value={{ basePath, token }}>
      {children}
    </ApiConfigContext.Provider>
  );
};

export const useApiConfig = (): ApiConfig => useContext(ApiConfigContext);
